import { useCallback, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { isAuthenticated, getUser } from '../services/auth';

// First segment that is an app route, not a tenant code. When URL is /super-admin/..., use session tenant instead.
const APP_PATH_SEGMENTS = new Set([
  'clients', 'client', 'providers', 'provider', 'payees', 'payee', 'contacts', 'contact',
  'schools', 'school', 'reports', 'referrals', 'referral', 'dashboard', 'work-tasks', 'settings', 'super-admin', 'login',
  'at-packets', 'file-reviews', 'my-checklists', 'client-services', 'transfers', 'about', 'help',
]);

/**
 * Returns tenant code from URL (when under /:tenantCode/...) and a helper to build tenant-prefixed paths.
 * When the URL first segment is an app path (e.g. super-admin), returns session tenant so nav and links show the real tenant.
 */
export function useTenantFromUrl() {
  const location = useLocation();
  const params = useParams();
  const pathname = location.pathname;
  const user = getUser();
  const sessionTenant = user?.tenant?.code;

  const urlTenantCode = params.tenantCode;
  const firstSegment = pathname.split('/').filter(Boolean)[0];
  const isOnLoginOrRoot =
    pathname === '/login' ||
    pathname === '/' ||
    pathname === '/about' ||
    pathname === '/help';

  let tenantCode =
    urlTenantCode ??
    (isAuthenticated() && !isOnLoginOrRoot ? firstSegment || sessionTenant : sessionTenant ?? null);
  if (tenantCode && APP_PATH_SEGMENTS.has(tenantCode) && sessionTenant) {
    tenantCode = sessionTenant;
  }

  /**
   * Build a path with tenant prefix. Pass path with or without leading slash.
   * When tenantCode is null (e.g. on login), returns path unchanged (for /login link).
   * Memoized so components using it in useEffect deps don't re-run every render.
   */
  const tenantPath = useCallback(
    (relativePath) => {
      const path = relativePath.startsWith('/') ? relativePath : `/${relativePath}`;
      if (!tenantCode) return path;
      return `/${tenantCode}${path}`;
    },
    [tenantCode]
  );

  return useMemo(
    () => ({ tenantCode, tenantPath }),
    [tenantCode, tenantPath]
  );
}
